/**
 * Dependency-free SSE parsing primitives for the kAI stream. Kept separate from
 * sse.js (which pulls in app config for the fetch call) so the parser can be
 * exercised in isolation. See contract §2 for the event schema.
 */

/**
 * Parse a single SSE event block (the text between two blank lines) into
 * `{ event, data }`. Follows the SSE field grammar: `event:` sets the event
 * name, `data:` lines accumulate (joined with `\n`), a leading space after the
 * colon is stripped, and comment lines (starting with `:`) are ignored.
 */
export function parseSSEEvent(block) {
  const lines = block.split('\n');
  let event = 'message';
  const dataLines = [];

  lines.forEach(line => {
    if (!line || line.startsWith(':')) return;

    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    let value = idx === -1 ? '' : line.slice(idx + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      dataLines.push(value);
    }
  });

  return { event, data: dataLines.join('\n') };
}

/**
 * Incremental SSE decoder. Buffers text across network chunks (a single event
 * can be split across multiple reads, and a single read can contain multiple
 * events) and emits complete events on the blank-line delimiter. CRLF is
 * normalised to LF so `\r\n\r\n` and `\n\n` both delimit. Does not rely on a
 * trailing newline — `flush()` emits any final buffered event.
 */
export function createSSEDecoder(onEvent) {
  let buffer = '';

  return {
    push(text) {
      buffer += text.replace(/\r\n/g, '\n');
      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (block.trim()) onEvent(parseSSEEvent(block));
        idx = buffer.indexOf('\n\n');
      }
    },
    flush() {
      if (buffer.trim()) onEvent(parseSSEEvent(buffer));
      buffer = '';
    },
  };
}
